/**
 * One answer, for the whole app, to "where is this region's ground, and where
 * is its water?"
 *
 * Two consumers have to agree about the region's **origin** — the point
 * everything is recentred on — or the map is a map of somewhere else: `/fly`
 * anchors its scenery, its relief and the sim's `GroundQuery` on it
 * (`flight/fly-region.ts`), and the in-flight sheet has to put the aeroplane's
 * recentred position back into the region's own frame (`map/map-model.ts`).
 * That origin used to be a pure function of the region body, so each side just
 * recomputed it. It no longer is: since `docs/engineering/london-sea-placements.md`
 * it depends on where the *water* is, which needs every placed object's
 * geometry. So it is computed once, here, and cached per region.
 *
 * ## What the origin is now
 *
 * `world/aerodrome-projection.ts` still fits the aerodromes' `mapX`/`mapY`
 * bounding box onto the region's placement bounding box — its *scale* is
 * sound. Each fitted position is then **anchored** onto the nearest scenery
 * placement with dry ground around it, and the start aerodrome's anchor is the
 * origin. That is fix 1a of `london-sea-placements.md` §5; fix 1b (refitting
 * the transform against `MAPn.DAT`) was evaluated first and does not hold up —
 * see `aerodrome-projection.ts`'s module note for the measurements.
 */

import { loadBakedModel, type BakedModelBody } from "../three/baked-model.ts";
import { ORIGIN_ZERO, placementWorldTransform, type PlacementOrigin } from "./placement-transform.ts";
import type { WorldRegionBody } from "./region-loader.ts";
import {
  anchorAerodromes,
  originOfSite,
  projectAerodromes,
  type AnchoredAerodrome,
  type GroundSite,
} from "./aerodrome-projection.ts";
import { buildWaterMask, recentreWaterMask, DRY_EVERYWHERE, type WaterMask } from "./water-mask.ts";

export interface RegionGround {
  /** What `placementWorldTransform` recentres against — `.WLD` axes, metres. */
  readonly origin: PlacementOrigin;
  /** The region's water, in the **recentred** world frame the scenery and the sim use. */
  readonly water: WaterMask;
  /** Every aerodrome, anchored onto real ground, in recentred world metres. */
  readonly aerodromes: readonly AnchoredAerodrome[];
  /** The one the region is recentred on, or `undefined` if the region has no field by that name. */
  readonly startAerodrome: AnchoredAerodrome | undefined;
}

/** Loads every distinct model a region places, through `loadBakedModel`'s promise cache. */
export async function loadRegionModels(region: WorldRegionBody): Promise<Map<string, BakedModelBody>> {
  const refs = [...new Set(region.placements.map((p) => p.objectRef).filter((r): r is string => r !== null))];
  const out = new Map<string, BakedModelBody>();
  await Promise.all(
    refs.map(async (ref) => {
      const body = await loadBakedModel(`${ref}.json`).catch(() => undefined);
      if (body) out.set(ref, body);
    }),
  );
  return out;
}

/** Every placement's ground point in the unrecentred world frame — the candidate anchors. */
function siteOf(region: WorldRegionBody): GroundSite[] {
  return region.placements.map((placement) => {
    const { position } = placementWorldTransform(placement);
    return { x: position.x, z: position.z };
  });
}

/**
 * The region's ground, given its models. Pure — the same body and models
 * always give the same origin, which is what lets a lockstep duel and a
 * replayed tape recentre identically.
 */
export function regionGroundFrom(
  region: WorldRegionBody,
  models: ReadonlyMap<string, BakedModelBody>,
  startAerodrome: string,
): RegionGround {
  // Built unrecentred, because the origin is not known until it has been used
  // to pick one; `recentreWaterMask` then shifts it rather than re-stamping
  // every triangle.
  const unrecentred = models.size > 0 ? buildWaterMask(region.placements, models) : DRY_EVERYWHERE;
  const anchored = anchorAerodromes(
    projectAerodromes(region.aerodromes, region.bounds),
    siteOf(region),
    unrecentred,
    startAerodrome,
  );
  const start = anchored.find((field) => field.name === startAerodrome);
  const origin = start ? originOfSite(start) : ORIGIN_ZERO;
  return {
    origin,
    water: recentreWaterMask(unrecentred, origin),
    aerodromes: anchored.map((field) => ({ ...field, x: field.x - origin.x, z: field.z + origin.z })),
    startAerodrome: start ? { ...start, x: 0, z: 0 } : undefined,
  };
}

const cache = new Map<string, Promise<RegionGround>>();

/**
 * `regionGroundFrom` with the models loaded and the result cached per
 * (region, start aerodrome). `/fly` and the map sheet both go through this, so
 * the sheet cannot disagree with the world it is a map of.
 */
export function loadRegionGround(region: WorldRegionBody, startAerodrome: string): Promise<RegionGround> {
  const key = `${region.region}|${startAerodrome}`;
  let ground = cache.get(key);
  if (!ground) {
    ground = loadRegionModels(region).then((models) => regionGroundFrom(region, models, startAerodrome));
    cache.set(key, ground);
  }
  return ground;
}
